import type {
  CartCalculationBundleResponse,
  CartCalculationItemResponse,
  CartCalculationOrderResponse,
  CartCalculationResponse,
} from '../Catalog';
import type { DeliverySlot, DeliverySlotsResponse } from '../ETA';
import type { NewOrderResponse } from '../Order';
import type {
  V3CalculateChildOrder,
  V3CalculatedBundle,
  V3CalculatedItem,
  V3CalculateResponse,
  V3NewResponse,
  V3PromocodeResult,
  V3RouteOption,
  V3SlotOption,
  V3Totals,
} from './types';

/**
 * Narrows calculate totals to the fields `orders/v3/new` declares: its schema
 * runs with `additionalProperties: false`, so read-only extras the calculate
 * response carries (e.g. `received_bonuses`) make the order request fail
 * validation. Fields absent from the response stay absent in the payload.
 */
export const pickV3NewOrderTotals = (totals: V3Totals): V3Totals => ({
  base_total: totals.base_total,
  discount_total: totals.discount_total,
  promocode_discount: totals.promocode_discount,
  paid_bonuses: totals.paid_bonuses,
  paid_loyalty_points: totals.paid_loyalty_points,
  paid_total: totals.paid_total,
  delivery_total: totals.delivery_total,
  tax_amount: totals.tax_amount,
  tax_percent: totals.tax_percent,
  municipality_tax_amount: totals.municipality_tax_amount,
  municipality_tax_percent: totals.municipality_tax_percent,
  service_fee: totals.service_fee,
  convertedTotal: totals.convertedTotal,
  convertedDiscountTotal: totals.convertedDiscountTotal,
  convertedPromocodeDiscount: totals.convertedPromocodeDiscount,
  convertedGoodsPaidTotal: totals.convertedGoodsPaidTotal,
  convertedPaidTotal: totals.convertedPaidTotal,
});

const adaptItem = (item: V3CalculatedItem): CartCalculationItemResponse =>
  ({
    actual_quantity: item.requested_quantity,
    image: item.image,
    is_gift: item.is_gift,
    name: item.name,
    paid_bonuses: item.paid_bonuses,
    parent_category_id: 0,
    product_id: Number(item.product_id) || 0,
    promo_quantity_discount_price: 0,
    promo_required_quantity: 0,
    requested_quantity: item.requested_quantity,
    sku: item.sku,
    weight: item.weight,
    // No `sellable`: V3 reports no stock, and `requested_quantity` here would
    // pin the cart's quantity limit to what is already in it.
    sellableWarehouses: {},
    base_price: item.base_price,
    discount_amount: item.discount_amount,
    promocode_discount_amount: item.promocode_discount_amount,
    paid_price: item.paid_price,
    is_discount_forbidden: false,
    discount_price: item.discount_price,
    salePrice: item.salePrice,
    convertedDiscountPrice: item.convertedDiscountPrice,
    tax_amount: 0,
    tax_percent: 0,
  }) as CartCalculationItemResponse;

const adaptBundle = (
  bundle: V3CalculatedBundle,
): CartCalculationBundleResponse => ({
  id: Number(bundle.id) || 0,
  requested_quantity: bundle.requested_quantity,
  price: 0,
  images: null,
  name: bundle.name,
});

const adaptChildOrder = (
  child: V3CalculateChildOrder,
  rootPromocode: V3PromocodeResult,
): CartCalculationOrderResponse => {
  const deliveryPrice =
    child.totals.delivery_total ?? child.route?.client_price ?? 0;
  return {
    warehouse_code: child.warehouse_code,
    items: child.items.map(adaptItem),
    bundles: child.bundles.map(adaptBundle),
    is_delivery_free: deliveryPrice === 0,
    seller: 'jiffy',
    address: { latitude: 0, longitude: 0 },
    received_bonuses: 0,
    base_received_bonuses: 0,
    bonuses_to_spend: 0,
    is_first_order: false,
    should_use_loyalty_points: false,
    should_use_bonuses: false,
    tax_percent: child.totals.tax_percent,
    tax_amount: child.totals.tax_amount,
    delivery_info: {
      base_price: deliveryPrice,
      discount_amount: 0,
      paid_bonuses: 0,
      paid_price: deliveryPrice,
      promocode_discount_amount: 0,
      slot_delivery_details: {
        schedule_slot_id: '',
        current_date: 0,
      },
      threshold: 0,
    },
    service_fee: child.totals.service_fee,
    customs_duties_fee: child.totals.customs_duties_fee,
    registration_fee: child.totals.registration_fee,
    customs_service_fee: child.totals.customs_service_fee,
    should_add_tax_to_paid_total: false,
    base_total: child.totals.base_total,
    discount_total: child.totals.discount_total,
    promocode_discount: child.totals.promocode_discount,
    paid_bonuses: child.totals.paid_bonuses,
    paid_total: child.totals.paid_total,
    paid_without_delivery: child.totals.paid_total - deliveryPrice,
    promocode: child.promocode ?? rootPromocode.promocode ?? null,
    promocodeError:
      child.promocodeError ?? rootPromocode.promocodeError ?? null,
    promocodeCalculationNote:
      child.promocodeCalculationNote ??
      rootPromocode.promocodeCalculationNote ??
      null,
  };
};

export const adaptV3SlotToDeliverySlot = (
  slot: V3SlotOption,
  route: V3RouteOption | undefined,
): DeliverySlot => ({
  ...slot,
  day_of_week: 0,
  delivery_price: route?.client_price ?? 0,
  free_delivery_threshold: 0,
  order_amount_threshold: null,
  reserved_capacity: 0,
  total_capacity: 0,
  is_active: true,
  company_id: '',
  zone_id: '',
});

export const adaptV3AvailableSlots = (
  order: V3CalculateChildOrder | undefined,
  selectedRoute: V3RouteOption | undefined,
): DeliverySlotsResponse => {
  const slots = order?.available_slots ?? [];
  return slots.reduce<DeliverySlotsResponse>((acc, slot) => {
    const key = `${slot.current_date * 1000}`;
    const bucket = acc[key] ?? [];
    bucket.push(adaptV3SlotToDeliverySlot(slot, selectedRoute));
    acc[key] = bucket;
    return acc;
  }, {});
};

/**
 * Detects blocking issues in V3 calculate response.
 * Returns a short reason code if checkout must be blocked, otherwise null.
 */
export const detectV3BlockingIssue = (
  v3: V3CalculateResponse,
): string | null => {
  if ((v3.data.minimal_order_value_error?.length ?? 0) > 0) {
    return 'MINIMAL_ORDER_VALUE_ERROR';
  }
  if (v3.data.issues?.some((i) => i.level === 'error')) {
    return 'CALCULATE_ISSUES_ERROR';
  }
  for (const order of v3.data.orders) {
    if (!order.route?.template_id) {
      return 'NO_DEFAULT_ROUTE';
    }
    if (order.route.slot_selection_required) {
      return 'SLOT_REQUIRED_NOT_SUPPORTED';
    }
    if (order.issues?.some((i) => i.level === 'error')) {
      return 'CHILD_ISSUES_ERROR';
    }
  }
  return null;
};

/**
 * Adapt LMS V3 calculate response to legacy V2 CartCalculationResponse shape
 * so that CatalogStore consumers (deliveriesOrders, totalCartPrice, UI) keep working.
 */
export const adaptV3CalculateToV2 = (
  v3: V3CalculateResponse,
): CartCalculationResponse => {
  const {
    totals,
    orders,
    minimal_order_value_error,
    promocode,
    promocodeError,
    promocodeCalculationNote,
  } = v3.data;
  const rootPromocode: V3PromocodeResult = {
    promocode,
    promocodeError,
    promocodeCalculationNote,
  };

  // `undefined` rather than 0 keeps the V2 meaning of "no such charge".
  const sumOverParcels = (
    pick: (order: V3CalculateChildOrder) => number | undefined,
  ) => {
    const total = orders.reduce((acc, order) => acc + (pick(order) ?? 0), 0);
    return total || undefined;
  };

  return {
    base_total: totals.base_total,
    discount_total: totals.discount_total,
    promocode_discount: totals.promocode_discount,
    paid_bonuses: totals.paid_bonuses,
    paid_loyalty_points: totals.paid_loyalty_points,
    paid_external_bonuses: 0,
    paid_total: totals.paid_total,
    convertedTotal: totals.convertedTotal,
    convertedDiscountTotal: totals.convertedDiscountTotal,
    convertedPaidTotal: totals.convertedPaidTotal,
    convertedPromocodeDiscount: totals.convertedPromocodeDiscount,
    customsDutiesFeeTotal: sumOverParcels((o) => o.totals.customs_duties_fee),
    registrationFee: sumOverParcels((o) => o.totals.registration_fee),
    customsDutiesServiceFee: sumOverParcels(
      (o) => o.totals.customs_service_fee,
    ),
    tax_amount: totals.tax_amount,
    tax_percent: totals.tax_percent,
    orders: orders.map((child) => adaptChildOrder(child, rootPromocode)),
    minimal_order_value_error: minimal_order_value_error as never,
  };
};

/**
 * Adapt LMS V3 new-order response to legacy V2 NewOrderResponse shape
 * so that CheckoutStore.processPayment, wallet handlers and Sentry breadcrumbs
 * keep reading the same fields (payment_intent.client_secret, providerPaymentDetails, etc).
 */
export const adaptV3NewToV2 = (v3: V3NewResponse): NewOrderResponse => {
  const topLevelPayment = v3.data.payment;

  const orders = v3.data.orders.map((entry) => ({
    order: {
      id: entry.order.id,
      order_id: entry.order.order_id,
      duration: {
        min: entry.order.duration,
        max: entry.order.duration,
      },
      seller: entry.order.seller,
      payment_method: entry.order.payment_method,
      company_id: entry.order.company_id,
      cancellationDelay: entry.order.cancellationDelay,
    },
    payment_intent: {
      client_secret:
        entry.payment_intent?.client_secret ||
        topLevelPayment?.client_secret ||
        undefined,
    },
    payment: {
      id: (entry.payment?.id as string) || topLevelPayment?.id || '',
      providerPaymentDetails:
        topLevelPayment?.providerPaymentDetails ?? undefined,
      myFatoorahPayment: { error: false },
    },
    payed_delivery: entry.payed_delivery,
    errors: entry.errors === false ? false : entry.errors,
  }));

  return {
    data: {
      multi_order: undefined,
      orders: orders as NewOrderResponse['data']['orders'],
    },
  };
};
